import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed calculateCameraTargetForItem - now using engine's safe teleportation

export const useGalleryInteractions = ({
  currentShowroomId,
  setCurrentShowroomId,
  showrooms,
  setSkipStartScreen,
  setShowStartScreen,
  handleCloseMenu,
  handleCloseConfirmation,
  handleShowItemDetails,
  handleCloseItemDetails,
  setFloatingMenuNavigation,
  engineRef,
  setSceneReady,
  loadingManager
}) => {
  const navigate = useNavigate();
  const pendingTeleportRef = useRef(null);

  // Function to handle pending teleportation after room load
  const processPendingTeleportation = useCallback(() => {
    if (pendingTeleportRef.current && engineRef.current) {
      const { item, placement } = pendingTeleportRef.current;
      console.log('🎯 Processing pending teleportation to item:', item.name);
      
      // Clear the pending teleportation first
      pendingTeleportRef.current = null;
      
      // Use the engine's teleportation method
      if (typeof engineRef.current.handleArtworkTeleportation === 'function') {
        // Add a slight delay to ensure the room is fully stabilized
        setTimeout(() => {
          engineRef.current.handleArtworkTeleportation(item, placement, true, false);
          console.log('✅ Cross-room teleportation completed');
        }, 500);
      } else {
        console.error('handleArtworkTeleportation method not available on engine');
      }
    }
  }, [engineRef]);

  const handleEngineInteraction = useCallback((type, data) => {
    if (type === 'artwork') {
      const { item, placement } = data;
      const itemShowrooms = [{
        id: currentShowroomId,
        name: showrooms.find(s => s.id === currentShowroomId)?.name,
        placement: placement
      }].filter(s => s.id);

      // Open FloatingMenu directly to catalog with item details
      setFloatingMenuNavigation({
        action: 'open',
        view: 'item-details',
        data: { item, showrooms: itemShowrooms }
      });
    }
  }, [currentShowroomId, showrooms, setFloatingMenuNavigation]);

  const handleTeleportToRoom = (roomId) => {
    // Track room teleportation operation
    const teleportOperationId = loadingManager?.startLoading(`Teleporting to room ${roomId}`);
    
    // Check if we can do instant switching (room is loaded)
    const canInstantSwitch = engineRef.current?.isRoomReady && engineRef.current.isRoomReady(roomId);
    
    if (canInstantSwitch) {
      // Instant switch - don't hide scene, just update room
      setCurrentShowroomId(roomId);
      navigate(`/gallery/${roomId}`);
      setSkipStartScreen(true);
      setShowStartScreen(false);
      
      // Close dialogs after a short delay to allow smooth transition
      setTimeout(() => {
        handleCloseMenu();
        handleCloseConfirmation();
      }, 100);
      
      // Complete operation quickly
      if (teleportOperationId && loadingManager?.finishLoading) {
        setTimeout(() => {
          loadingManager.finishLoading(teleportOperationId);
        }, 100);
      }
    } else {
      // Room not ready - show loading screen and load normally
      setSceneReady(false);
      setCurrentShowroomId(roomId);
      navigate(`/gallery/${roomId}`);
      setSkipStartScreen(true);
      setShowStartScreen(false);
      handleCloseMenu();
      handleCloseConfirmation();
      
      // Complete operation after room loads
      if (teleportOperationId && loadingManager?.finishLoading) {
        setTimeout(() => {
          loadingManager.finishLoading(teleportOperationId);
        }, 500);
      }
    }
  };

  const handleTeleportToItem = useCallback((item, placement, targetRoomId = null) => {
    // Handle cross-room teleportation
    if (targetRoomId && targetRoomId !== currentShowroomId) {
      console.log(`🚪 Cross-room teleportation: ${item.name} in room ${targetRoomId}`);
      
      // Store the teleportation details for later processing
      pendingTeleportRef.current = { item, placement };
      
      // Show loading screen immediately
      setSceneReady(false);
      
      setCurrentShowroomId(targetRoomId);
      navigate(`/gallery/${targetRoomId}`);
      setSkipStartScreen(true);
      setShowStartScreen(false);
      handleCloseMenu();
      handleCloseConfirmation();
      handleCloseItemDetails(); // Close item popup if open
      
      // The pending teleportation will be processed when the scene is ready
      // via the processPendingTeleportation function
      return;
    }
    
    // Same room teleportation
    setSkipStartScreen(true);
    setShowStartScreen(false);
    handleCloseMenu();
    handleCloseConfirmation();
    handleCloseItemDetails(); // Close item popup if open
    
    // Use the engine's safe teleportation method immediately
    setTimeout(() => {
      if (engineRef.current) {
        // Use the safe teleportation method with fromCatalog=true, showPopup=false
        if (typeof engineRef.current.handleArtworkTeleportation === 'function') {
          engineRef.current.handleArtworkTeleportation(item, placement, true, false); // fromCatalog=true, showPopup=false
        } else {
          console.error('handleArtworkTeleportation method not available on engine');
        }
        
        // Teleportation complete - NO POPUP from catalog
      }
    }, 100);
  }, [currentShowroomId, setCurrentShowroomId, navigate, setSkipStartScreen, setShowStartScreen,
      handleCloseMenu, handleCloseConfirmation, handleCloseItemDetails, engineRef, showrooms, handleShowItemDetails, setSceneReady]);

  return {
    handleEngineInteraction,
    handleTeleportToRoom,
    handleTeleportToItem,
    processPendingTeleportation
  };
};